import { ArrayUtility } from '@solcre-org/core';
import { bookingCodeTemplate } from '../templates/bookingCodeTemplate';
import { clientUriTemplate } from '../templates/clientUriTemplate';
import { currencyUriTemplate } from '../templates/currencyUriTemplate';
import { portUriTemplate } from '../templates/portUriTemplate';
import { voyageUriTemplate } from '../templates/voyageUriTemplate';
import { DateUtils } from '../utils/date.utils';
import { environment } from '../../../environments/environment.local';
import { BookingAttachmentModel } from './booking-attachment.model';
import { ClientModel } from './client.model';
import { CurrencyModel } from './currency.model';
import { PortModel } from './port.model';
import { VoyageModel } from './voyage.model';
import { VesselModel } from './vessel.model';

export class BookingModel {
  private apiConnectionType?: 'direct' | 'tbn' | 'continuity' | 'transshipment';

  public clientDetails?: ClientModel;
  public voyageDetails?: VoyageModel;
  public originPortDetails?: PortModel;
  public destinationPortDetails?: PortModel;
  public transshipmentPortDetails?: PortModel;
  public secondVoyageDetails?: VoyageModel;
  public currencyDetails?: CurrencyModel;
  public attachments?: BookingAttachmentModel[] = [];
  public requiresTransshipmentToggle?: boolean;

  /**
   * Whether this booking requires transshipment.
   *
   * Prefers the explicit `requiresTransshipmentToggle` flag when set.
   * Falls back to inferring from the presence of `secondVoyage` or `transshipmentPort`
   * to maintain compatibility with older records that lack the explicit flag.
   */
  public get requiresTransshipment(): boolean {
    if (this.requiresTransshipmentToggle !== undefined && this.requiresTransshipmentToggle !== null) {
      return this.requiresTransshipmentToggle;
    }
    return !!(this.secondVoyage || this.transshipmentPort);
  }

  public get connectionType(): 'direct' | 'tbn' | 'continuity' | 'transshipment' {
    if (this.apiConnectionType) {
      return this.apiConnectionType;
    }

    if (!this.secondVoyage && !this.transshipmentPort) {
      return 'direct';
    }

    if (!this.secondVoyage || this.secondVoyage === 'TBN') {
      return 'tbn';
    }

    const firstVessel =
      this.voyageDetails?.vesselDetails?.id?.toString() ??
      this.voyageDetails?.vesselDetails?.vesselName ??
      this.voyageDetails?.vessel;
    const secondVessel =
      this.secondVoyageDetails?.vesselDetails?.id?.toString() ??
      this.secondVoyageDetails?.vesselDetails?.vesselName ??
      this.secondVoyageDetails?.vessel;

    return firstVessel && secondVessel && firstVessel === secondVessel
      ? 'continuity'
      : 'transshipment';
  }

  public get connectionTypeLabelKey(): string {
    return `bookings.connectionTypes.${this.connectionType}`;
  }

  public cargoOperationalStatus?: 'to_be_loaded' | 'on_board' | 'discharged';

  public get cargoStatusLabelKey(): string | undefined {
    return this.cargoOperationalStatus
      ? `bookings.cargoStatuses.${this.cargoOperationalStatus}`
      : undefined;
  }

  constructor(
    public id?: number,
    public bookingCode?: string,
    //Embedded Start
    public client?: string,
    public voyage?: string,
    public shipper?: string,
    public consignee?: string,
    public originPort?: string,
    public destinationPort?: string,
    public transshipmentPort?: string | null,
    public secondVoyage?: string | null,
    public currency?: string,
    //Embedded End
    public paymentTerms?: string,
    public billOfLading?: string,
    public status?: string,
    public totalCubicMeters?: number,
    public totalFreight?: number,
    public totalLinearMeters?: number,
    public totalSquareMeters?: number,
    public totalWeight?: number,
    public requestDate?: string,
    public revenue?: number,
  ) { }

  public fromJSON(json: any): BookingModel {
    if (json) {
      this.id = json.id;
      this.bookingCode = json.bookingCode;
      this.paymentTerms = json.paymentTerms;
      this.billOfLading = json.billOfLading;
      this.shipper = json.shipper;
      this.consignee = json.consignee;
      this.client = json.client;
      this.status = json.status;
      this.totalCubicMeters = json.totalCubicMeters;
      this.totalFreight = json.totalFreight;
      this.totalLinearMeters = json.totalLinearMeters;
      this.totalSquareMeters = json.totalSquareMeters;
      this.totalWeight = json.totalWeight;
      this.requestDate = DateUtils.cleanApiDate(json.requestDate);
      this.attachments = [];
      this.revenue = json.revenue;
      this.apiConnectionType = json.connectionType;
      this.cargoOperationalStatus = json.cargoOperationalStatus;
      this.requiresTransshipmentToggle = json.requiresTransshipment != null
        ? !!json.requiresTransshipment || !!json.isTbn
        : !!(json.secondVoyage || json.transshipmentPort || json.isTbn);

      //Client check
      if (typeof json.client === 'object') {
        this.clientDetails = new ClientModel().fromJSON(json.client);
        this.client = clientUriTemplate(this.clientDetails.id?.toString());
      } else {
        this.clientDetails = undefined;
        this.client = json.client;
      }

      //Client voyage
      if (typeof json.voyage === 'object') {
        this.voyageDetails = new VoyageModel().fromJSON(json.voyage);
        this.voyage = voyageUriTemplate(this.voyageDetails.id?.toString());
      } else {
        this.voyageDetails = undefined;
        this.voyage = json.voyage;
      }

      //Client originPort
      if (typeof json.originPort === 'object') {
        this.originPortDetails = new PortModel().fromJSON(json.originPort);
        this.originPort = portUriTemplate(
          this.originPortDetails.id?.toString()
        );
      } else {
        this.originPortDetails = undefined;
        this.originPort = json.originPort;
      }

      //Client destinationPort
      if (typeof json.destinationPort === 'object') {
        this.destinationPortDetails = new PortModel().fromJSON(
          json.destinationPort
        );
        this.destinationPort = portUriTemplate(
          this.destinationPortDetails.id?.toString()
        );
      } else {
        this.destinationPortDetails = undefined;
        this.destinationPort = json.destinationPort;
      }

      if (typeof json.transshipmentPort === 'object') {
        this.transshipmentPortDetails = new PortModel().fromJSON(
          json.transshipmentPort
        );
        this.transshipmentPort = portUriTemplate(
          this.transshipmentPortDetails.id?.toString()
        );
      } else {
        this.transshipmentPortDetails = undefined;
        this.transshipmentPort = json.transshipmentPort;
      }

      if (typeof json.secondVoyage === 'object' && json.secondVoyage !== null) {
        this.secondVoyageDetails = new VoyageModel().fromJSON(json.secondVoyage);
        this.secondVoyage = voyageUriTemplate(this.secondVoyageDetails.id?.toString());
      } else {
        this.secondVoyageDetails = undefined;
        this.secondVoyage = json.secondVoyage;
      }

      // Auto-assign 'TBN' if isTbn flag is true or transshipment is required but no second voyage exists
      if (json.isTbn || (this.requiresTransshipmentToggle && !this.secondVoyage)) {
        this.secondVoyage = 'TBN';
      }

      //Client currency
      if (typeof json.currency === 'object') {
        this.currencyDetails = new CurrencyModel().fromJSON(json.currency);
        this.currency = currencyUriTemplate(
          this.currencyDetails.id?.toString()
        );
      } else {
        this.currencyDetails = undefined;
        this.currency = json.currency;
      }

      //Attachments
      ArrayUtility.each(json.attachments, (attachment: any) => {
        const attach: BookingAttachmentModel = new BookingAttachmentModel();
        attach.fromJSON(attachment);
        this.attachments?.push(attach);
      });
    }
    return this;
  }

  public fromJSONList(json: any): BookingModel[] {
    if (json) {
      return json.map((element: any) => new BookingModel().fromJSON(element));
    }
    return [];
  }

  public toJSON() {
    return {
      id: this.id,
      bookingCode: this.bookingCode
        ? this.bookingCode
        : bookingCodeTemplate({}),
      billOfLading: this.billOfLading,
      shipper: this.shipper,
      consignee: this.consignee,
      voyage: this.voyage,
      secondVoyage: this.requiresTransshipmentToggle ? (this.secondVoyage === 'TBN' ? null : this.secondVoyage) : null,
      originPort: this.originPort,
      destinationPort: this.destinationPort,
      transshipmentPort: this.requiresTransshipmentToggle ? this.transshipmentPort : null,
      currency: this.currency,
      paymentTerms: this.paymentTerms,
      status: this.status,
      totalCubicMeters: this.totalCubicMeters,
      totalFreight: this.totalFreight,
      totalLinearMeters: this.totalLinearMeters,
      totalSquareMeters: this.totalSquareMeters,
      totalWeight: this.totalWeight,
      client:
        this.client && !this.client.toString().includes(environment.API.CLIENTS_URI)
          ? clientUriTemplate(this.client.toString())
          : this.client,
      requestDate: this.requestDate,
      revenue: this.revenue,
      isTbn: this.requiresTransshipmentToggle ? this.secondVoyage === 'TBN' : false,
      requiresTransshipment: this.requiresTransshipmentToggle ? this.secondVoyage !== 'TBN' : false,
    };
  }

  public getId() {
    return this.id ?? '';
  }

  public getReference() {
    return this.id ?? '';
  }
}
